import React, { useState, useEffect } from "react";
import { Button } from "@material-ui/core";

import * as socket from "socket";
import { IndexedGames, PresentationFields } from "types/game";
import { AdvertTypes } from "types/advert";
import { exportStartSheetToBase64 } from "utils/GameReportGenerator/pdf";
import AdvertSection from "components/Adverts/GameAdvert";
import { useAdMediaQuery } from "hooks/useAdMediaQuery";
import StartSheetHeader from "./StartSheetHeader";
import StartSheetTable from "./StartSheetTable";
import { useSelector } from "hooks/useSelector";
import { User } from "types/user";
import MobileStartSheet from "./MobileStartSheet";
import gamesApi from "api/games";
import { showSnackbar } from "components/SharedComponents/Notifier";

interface StartSheetProps {
  gameId: string;
  games: IndexedGames;
  downloadable?: boolean;
  onSwitchClick?: VoidFunction;
}

export const StartSheet: React.FC<StartSheetProps> = ({ gameId, games, downloadable, onSwitchClick }) => {
  const [landscape] = useAdMediaQuery();
  const [game, setGame] = useState(games[gameId]);
  const widescreen = game.startSheet?.widescreen && landscape;

  const user = useSelector(state => state.auth.user);

  const isOrganiser = game.organizers.find(o => o._id === user._id) !== undefined;

  useEffect(() => {
    if (socket.connect(gameId)) {
      socket.addEvent(socket.EVENTS.PRESENTATION_UPDATED, ({ startSheet }: { startSheet: PresentationFields }) =>
        setGame(g => ({ ...g, startSheet })),
      );
    }

    return () => {
      socket.disconnect();
    };
  }, [gameId]);

  return (
    <div
      style={{
        ...(widescreen
          ? {
            width: "100%",
            height: "100vh",
            overflow: "auto",
          }
          : {
            minWidth: window.innerWidth < 540 ? window.innerWidth : 540,
            overflow: "hidden",
          }),
        display: "flex",
        flex: "1",
        height: widescreen ? "calc(100vh - 64px)" : "auto",
        minWidth: window.innerWidth < 540 ? window.innerWidth : 540,
        flexDirection: "column",
        position: "relative",
      }}
    >
      {!widescreen && <StartSheetHeader gameId={gameId} games={games} />}
      {!widescreen && <MobileStartSheet gameId={gameId} games={games} />}
      <StartSheetTable
        isOrganiser={isOrganiser}
        game={game}
        from={0}
        userState={user}
        onSwitchClick={onSwitchClick}
        widescreen={widescreen}
      />
      {downloadable && !widescreen && (
        <Button
          fullWidth
          variant={"contained"}
          style={{ position: "sticky", bottom: 0 }}
          color={"primary"}
          onClick={async () => await downloadStartSheetPDF(gameId, games, isOrganiser, user)}
        >
          Download PDF
        </Button>
      )}
      {widescreen && <AdvertSection type={AdvertTypes.START_SHEET} gameId={gameId} clubLogo={game.course.club.logo} />}
    </div>
  );
};

export const downloadStartSheetPDF = async (
  gameId: string,
  games: IndexedGames,
  isOragniser: boolean,
  user: User,
  returnPdf?: boolean,
) => {
  let game = games[gameId];

  try {
    const response = await gamesApi.getGamePlayers(gameId);
    const playersList = Array.isArray(response.data?.players)
      ? response.data.players
      : Array.isArray(response.data)
      ? response.data
      : [];

    if (!playersList.length) {
      throw new Error("No players found for this game.");
    }

    const fetchedPlayers: IndexedGames[string]["players"] = {};
    playersList.forEach((p: any) => {
      if (p?._id) fetchedPlayers[p._id] = p;
    });

    if (Object.keys(fetchedPlayers).length === 0) {
      throw new Error("No valid players found for this game.");
    }

    game = { ...game, players: fetchedPlayers };
    console.log(`[downloadStartSheetPDF] Fetched ${Object.keys(fetchedPlayers).length} players for game ${gameId}`);
  } catch (error) {
    console.error(`[downloadStartSheetPDF] Failed to refresh players for game ${gameId}:`, error);
    showSnackbar("Failed to refresh players before generating the Start Sheet PDF. Please try again.", 5000, "error");
    return "";
  }

  if (returnPdf) {
    return exportStartSheetToBase64(game, { includeUnpaidPlayers: isOragniser });
  }

  // For direct download, use the data-driven PDF generator
  // and trigger a browser download
  const base64Pdf = exportStartSheetToBase64(game, { includeUnpaidPlayers: isOragniser });
  if (base64Pdf) {
    const link = document.createElement("a");
    link.href = base64Pdf;
    link.download = `${game.name}-startSheet.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  return "";
};
